import React from 'react';
import moment from 'moment';
import { CircleAlert } from 'lucide-react';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesHistoryTimeline,
  type AgnesHistoryTimelineItem,
} from '@agnes/ui';
import ServiceRubricaTabela, {
  type RubricaHistoricoAlteracao,
  type RubricaHistoricoItem,
  type RubricaTabelaItem,
} from '@/services/ServiceRubricaTabela';

const dataQa = 'rubricas-tabela-historico';
const PAGE_SIZE = 50;

// Evento fixo da recepção: só a data (dh_recepcao do S-1010) muda.
const RECEPCAO_TITULO = 'Rubrica recebida via S-1010';
const RECEPCAO_ORIGEM = 'Recepção eSocial';

// Com offset (updatedAt, em UTC) converte para o horário local; sem offset (dhRecepcao,
// horário do eSocial) exibe como veio.
const fmtQuando = (v: string | null) => {
  if (!v) return null;
  const m = moment(v, moment.ISO_8601);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : v;
};

// Mesmo aviso da listagem de Afastamento para valor vazio.
const CAMPO_SEM_PREENCHIMENTO = 'Campo sem preenchimento no XML';

const fmtValor = (valor: string | null, descricao: string | null) =>
  descricao ||
  valor || (
    <span className="inline-flex items-center gap-1.5 align-middle text-agnes-error">
      <CircleAlert className="h-4 w-4 shrink-0" /> {CAMPO_SEM_PREENCHIMENTO}
    </span>
  );

const Alteracao = ({ alteracao }: { alteracao: RubricaHistoricoAlteracao }) => (
  <p className="text-sm text-agnes-text">
    <span className="font-semibold">
      {alteracao.changedFieldDescription ?? alteracao.changedField ?? 'Alteração'}:
    </span>{' '}
    {fmtValor(alteracao.fromValue, alteracao.fromValueDescription)} →{' '}
    {fmtValor(alteracao.toValue, alteracao.toValueDescription)}
  </p>
);

type Evento = AgnesHistoryTimelineItem;

const eventoEdicao = (item: RubricaHistoricoItem): Evento => ({
  key: `edicao-${item.id}`,
  content: item.changes.map((alteracao) => <Alteracao key={alteracao.id} alteracao={alteracao} />),
  detail: [item.updatedByName ?? item.updatedBy, fmtQuando(item.updatedAt)]
    .filter(Boolean)
    .join(' · '),
});

const eventoRecepcao = (dhRecepcao: string): Evento => ({
  key: 'recepcao',
  content: <p className="text-sm font-semibold text-agnes-text">{RECEPCAO_TITULO}</p>,
  detail: [RECEPCAO_ORIGEM, fmtQuando(dhRecepcao)].filter(Boolean).join(' · '),
});

// Timeline de histórico de recomendações da rubrica. Extraído do modal da CON-5370
// (ModalHistoricoRubrica) para ser apresentado como aba na página de análise (CON-5377).
export function HistoricoRubrica({ rubrica }: { rubrica: RubricaTabelaItem }) {
  // Objeto novo a cada pedido: repetir a mesma página (nova tentativa) refaz a busca.
  const [pedido, setPedido] = React.useState({ page: 0 });
  const [paginasCarregadas, setPaginasCarregadas] = React.useState(0);
  const [rows, setRows] = React.useState<RubricaHistoricoItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [dhRecepcao, setDhRecepcao] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(false);
    ServiceRubricaTabela.historico(rubrica.idAnaliseRubrica, pedido.page, PAGE_SIZE)
      .then(({ data }) => {
        if (ignore) return;
        const content = data.content ?? [];
        setRows((prev) => (pedido.page === 0 ? content : [...prev, ...content]));
        setTotal(data.totalElements ?? 0);
        setDhRecepcao(data.dhRecepcao ?? null);
        setPaginasCarregadas(pedido.page + 1);
      })
      .catch(() => {
        if (!ignore) setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [rubrica.idAnaliseRubrica, pedido]);

  const carregarMais = () => setPedido({ page: paginasCarregadas });
  const temMais = rows.length < total;

  // A recepção é o evento mais antigo: entra no fim, depois da última página.
  const eventos = rows.map(eventoEdicao);
  if (paginasCarregadas > 0 && !temMais && dhRecepcao) eventos.push(eventoRecepcao(dhRecepcao));

  // Erro do carregamento inicial (sem itens) é da tela — o AgnesHistoryTimeline
  // só trata vazio/loading/paginação. A falha ao paginar (com itens) fica no componente.
  if (eventos.length === 0 && error && !loading) {
    return (
      <div className="py-2">
        <AgnesAlert preset="error" dataQa={dataQa}>
          <AgnesAlertContent dataQa={dataQa}>
            Não foi possível carregar o histórico da rubrica.
          </AgnesAlertContent>
        </AgnesAlert>
      </div>
    );
  }

  return (
    <div className="py-2">
      <AgnesHistoryTimeline
        items={eventos}
        dataQa={dataQa}
        emptyTitle="Nenhuma alteração registrada"
        loading={loading}
        hasMore={temMais}
        onLoadMore={carregarMais}
      />
    </div>
  );
}
